import $ from 'jquery';
import _ from 'underscore';
import autosize from 'lib/autosize';
import wafer from 'lib/wafer';
import ajax from 'util/ajax';
import analytics from 'util/analytics';
import { initAutosubmit } from 'util/autosubmit';
import {
  EVT_QUIZ_POSITION_DID_CHANGE,
  QUIZ_CHAPTER_INTRO_STATE,
  CLASS_NAME_DISABLED,
  CLASS_NAME_LOADING,
  CLASS_NAME_ACTIVE,
  CMP_RELOADING_FLAGS,
} from 'util/constants';
import * as deferred from 'util/deferred';
import flags from 'util/flags';
import {
  $body,
  $window,
  appVersion,
  jqGetElemByCmpName,
  jqGetElemById,
  instantTryItYourself,
  isIos,
  isMobile,
  isNative,
  jqOffOn,
  mediumPadding,
  setScrollPosition,
  userIsAuthenticated,
} from 'util/global';
import * as latex from 'util/latex';
import * as mathjaxLoader from 'util/mathjax_loader';
import modalHelpers from 'util/modal_helpers';
import * as Sentry from '@sentry/browser';
import sticky from 'util/sticky';
import * as urls from 'util/urls';
import {
  Anchors,
  getScrollHeight,
  keyboardNavigation,
  showConfirmationModal,
  showGenericModal,
  toggleButtonLoadingState,
} from 'util/ui';
import { controller as codexController } from 'app/codex/controllers';
import comments from 'app/comments';
import contentModal from 'app/content_modal';
import { showTimeoutErrorModal, errorModalHandler } from 'app/error_modal';
import { initGenericControllers } from 'app/generic_controllers';
import preloaders from 'app/preloaders';
import { images as zoomableImages } from 'app/zoomable';

/** @module  */
const solvable = {};

/**
 * Root object for cached or reusable solvable elements and data. Elements are grouped by
 * solvable id like so:
 * solvableContext = {
 *     <solvable id>: {
 *         $solvable: <solvable object>,
 *         data: <solvable data>,
 *         ...
 *     }
 * }
 * @type {Object}
 */
var solvableContext = {};

/** @type {Boolean} */
var isLoggedOutPractice = false;

/** @type {Number} */
var problemsLeftBeforeSignup = null;

/** @type {Boolean} */
var insterstitialPaneReloaded = false;

// Alex (11/6/2017): used for logging (BP-14763), remove once it's not needed anymore
var clickedChoice;

/**
 * Given an element with a data-solvable attribute, return the solvableContext context object
 * @param {JQuery} $element
 * @return {Object}
 */
var findSolvableContext = function ($element) {
  return solvableContext[$element.data('solvable')] || {};
};

/**
 * The onload/onready function for solvables and solvable pages. Use this
 * instead of $(function () { ... });
 */
var solvableOnload = function (ctx) {
  // solution discussions
  comments.add_form();
  comments.votes();
};

/**
 * Loads the recently solved by component and caches the result
 */
var loadSolversComponent = function (ctx) {
  // check that we haven't already loaded this component
  if (ctx.$solversList) {
    const $thisSolversList = ctx.$solversList;
    solvable.recentSolvers($thisSolversList);
  }
  // load the component
  if (ctx.$solversContainer) {
    deferred.load(ctx.$solversContainer.children('.cmp-deferred'));
  }
};

/**
 * Changes the solvable state
 * @param {string} [state] - one of availableSolvableStates or 'back', or 'default'.
 *                           If 'default' or not provided, solvable will reset to the default
 *                           state (question with detail sidebar)
 */
var setSolvableState = (function () {
  var availableSolvableStates = ['solutions', 'disputes'];
  return function (state, ctx, needToScroll = true) {
    state = state || 'default';
    ctx.previousState = ctx.previousState || null;
    ctx.currentState = ctx.currentState || null;

    if (state === 'back') {
      setSolvableState(ctx.previousState, ctx);
      return;
    }

    // reset the solvable state
    ctx.$solvable.removeClass(availableSolvableStates.join(' '));

    // Remove the wiki hash and button listener
    modalHelpers.removeBackButton();

    // update the state if provided
    if (state && availableSolvableStates.indexOf(state) >= 0) {
      ctx.$solvable.addClass(state);
    }

    // disputes are visually the same as solutions for now
    if (state === 'solutions' || state === 'disputes') {
      handleSolvDiscState(ctx, state, needToScroll);
      if (state === 'disputes') {
        const $disputes = $('.solv-disputes');
        if (isMobile) {
          $disputes.removeClass('hide');
        }
        if (needToScroll) {
          Anchors.scrollToTarget($disputes);
        }
      }
    } else if (
      needToScroll &&
      state === 'default' &&
      ctx.currentState === 'solutions' &&
      !isMobile
    ) {
      Anchors.scrollToTarget($body);
    }

    // scale large equations
    scaleSolvableContent(ctx);

    // deal with math in MCQ buttons
    scaleMcqSvgs(ctx);

    // try to update mathjax
    latex.updateMathjax(ctx.$solvable);

    ctx.previousState = ctx.currentState;
    ctx.currentState = state;

    $body.trigger($.Event('solvable.statechange', ctx));
  };
})();

/**
 * External method for setting solvable state
 * @param {JQuery} $solvableElement
 * @param {String} state
 */
solvable.setStateForSolvable = function ($solvableElement, state, needToScroll = true) {
  state = state || null;
  setSolvableState(state, findSolvableContext($solvableElement), needToScroll);
};

/**
 * Attach scroll events to the modal container to determine when to load deferred components
 * @param {JQuery} $container - modal container
 * @param {number} numComponents - number of deferred components
 */
function handleModalSolutionsDeferredComponents($container, numComponents) {
  // wait for rendering to finish before we start calling AJAX
  _.defer(deferred.addCmpsInContainer, $container, numComponents);

  $container.on(
    'scroll.deferred',
    _.throttle(e => {
      deferred.addCmpsInContainer($(e.target) || null, numComponents);
    }, 50),
  );
}

/**
 * Handle switching to solution or dispute view
 */
var handleSolvDiscState = function (ctx, state, needToScroll = true) {
  // no $solvable context in discussions webview
  ctx.$solvable = ctx.$solvable || $('.solv-main');

  var $discussions = ctx.$solvable.find(
      state === 'solutions' ? '.solv-solutions' : '.solv-disputes',
    ),
    $simpleSolutions = ctx.$solvable.find('#solv-simple-solution-target'),
    $deferredComponents = $discussions.find('.cmp-deferred');

  ctx.$solvable.find('.btn-solution.wiggle-less').removeClass('wiggle-less active-green').blur();

  ctx.$solvable.find('.btn-solution.wiggle').removeClass('wiggle').blur();

  // for mobile practice solutions, scroll to the solution when clicking the button
  if (isMobile && $simpleSolutions.length > 0) {
    if (needToScroll) {
      Anchors.scrollToTarget($simpleSolutions);
    }
    return;

    // mobile solution discussions
  } else if (state === 'solutions' && isMobile && $simpleSolutions.length === 0) {
    let $component = jqGetElemByCmpName('solutiondiscussions_publicsolvableaddsolutionpost');
    if (!$component.length) {
      $component = jqGetElemByCmpName('solutiondiscussions_publicsolvablesolutionlist');
    }
    if (needToScroll && $component.length) {
      Anchors.scrollToTarget($component);
    }
  }

  if ($discussions.length > 0) {
    // deferred components
    $deferredComponents.each((i, cmp) => {
      deferred.add($(cmp));
    });

    const $container = ctx.$solvable.parents('#nf-feeditem-modal');
    if ($container.length > 0) {
      handleModalSolutionsDeferredComponents($container, $deferredComponents.length);
    }

    // re-init autosized textarea
    autosize($discussions.find('textarea:visible'));
  }

  if (needToScroll) {
    Anchors.scrollToTarget(
      $(
        '#solutions-scrollable-container,#disputes-scrollable-container,#solv-simple-solution,#b2-solution',
      ).filter(':visible'),
    );
  }
};

/** Resize oversized math */
const scaleSolvableContent = ctx => {
  const { $solvable } = ctx;
  latex.scaleSVG($solvable, isMobile ? 0.4 : undefined);
};

/**
 * Scale SVG math in MCQ buttons
 */
function scaleMcqSvgs(ctx) {
  latex.scaleSVG(ctx.$solvable.find('.btn-mcqs'));
}

/**
 * Runs after loading solution dicussions in cases where the solution button is outside of
 * the solvable component
 * @param {JQuery} $btn
 * @param {boolean} solutionsLoaded
 */
var resetSolutionButton = function ($btn, solutionsLoaded) {
  $btn = $btn || $('[data-controller].dim');
  if ($btn.length) {
    $btn.removeClass('dim disabled').removeAttr('disabled');
    $btn.data('solutions-loaded', solutionsLoaded);
    const $btnContent = isMobile ? $btn.find('.main') : $btn;
    toggleButtonLoadingState($btnContent, false);
  }
};

/**
 * Handler for solution button controller that only needs to show already-loaded solutions
 */
var handlePassiveSolutionButton = function (ctx) {
  if (ctx.currentState === 'solutions') {
    ctx.$solvable.find('.solv-details .btn-solution').removeClass('active');
    setSolvableState('', ctx);
    $window.trigger($.Event('solution.hide'));
  } else {
    ctx.$solvable.find('.solv-details .btn-solution').removeClass('btn-accent').addClass('active');
    ctx.$solvable.find('.esr-problem').addClass('btn-accent active');
    setSolvableState('solutions', ctx);
    $window.trigger($.Event('solution.show'));
  }
};

/**
 * Handler for solution button controller that needs to load solutions
 * @param {object} data - button data attributes
 */
var handleActiveSolutionButton = function (data) {
  var ctx = solvableContext[data.solvable];
  if (!ctx.solutionsLoading) {
    ctx.solutionsJustLoaded = true;
    ctx.solutionsLoading = true;
    ajax(data.page_key).post(data.postLink, data);
  }
};

/**
 * Main solvable controller
 * @param {JQuery} $s - the main solvable container
 */
solvable.solvable = function ($s) {
  var $solvable = $s,
    solvableId = $solvable.data('solvable'),
    ctx = solvableContext[solvableId] || {};

  // update context
  ctx.$solvable = $solvable;
  ctx.data = jqGetElemById('solvable-data').data();
  ctx.solvableDidOnload = ctx.solvable === true;
  ctx.currentState = null;
  ctx.previousState = null;
  solvableContext[solvableId] = ctx;

  jqGetElemById('direct-link-input').on('click', e => {
    e.stopPropagation();
    $(e.currentTarget).select();
  });

  // resize svg where possible
  scaleSolvableContent(ctx);

  if (ctx.solutionsJustLoaded) {
    solvableContext[solvableId].solutionsJustLoaded = false;
    ctx.solutionsJustLoaded = false;
    setSolvableState('solutions', ctx);
  } else if (ctx.disputesJustLoaded) {
    $('.dispute-disc-modal, .reveal-disputes-modal').modal('hide');
    solvableContext[solvableId].disputesJustLoaded = false;
    ctx.disputesJustLoaded = false;
    setSolvableState('disputes', ctx);
  } else if (
    isMobile &&
    userIsAuthenticated &&
    ctx.$solvable.find('#solv-simple-solution').length
  ) {
    setSolvableState('solutions', ctx);
  }

  // update mathjax is there's any mathjax
  latex.updateMathjax($solvable.find('.uses-latex, .latex'));

  if (ctx.solvableDidOnload !== true) {
    solvableContext[solvableId].solvableDidOnload = true;
    solvableOnload(ctx);
  }

  // check instant response data
  const instantResponseData = $solvable.data('ir-verification');

  // only process if there's data and we can track it (and we actually showed an instant response)
  if (instantResponseData && !instantResponseData.preventUpdate) {
    var $sidebar = $solvable.find('.solv-details'),
      matchIsEndstate = instantResponseData.canStillAnswer === !$sidebar.hasClass('endstate'),
      matchIsCorrect = instantResponseData.answerIsCorrect === $sidebar.hasClass('correct'),
      matchAttemptNumber =
        instantResponseData.attemptNumber ===
        $solvable.find('#attempt-number').data('attempt-number');

    // if there's a mismatch
    if (!matchIsEndstate || !matchIsCorrect || !matchAttemptNumber) {
      analytics.track('instant response mismatch', {
        solvable: solvableId,
        answer: encodeURIComponent(instantResponseData.answer),
        url: window.location.href,

        // happens when one shows and endstate and the other does not
        'endstate mismatch': !matchIsEndstate,

        // happens when one shows correct and the other show incorrect or not answered
        'correct/incorrect state mismatch': !matchIsCorrect,

        // happens when attempt number is out of sync (i.e. 1/3 vs 2/3 attempts)
        'attempt number mismatch': !matchAttemptNumber,
      });
    }
    $solvable.removeData('ir-verification');
  }
};

solvable.solvableRedesign = function ($solvable) {
  const solvableId = $solvable.data('solvable');
  const ctx = solvableContext[solvableId] || {};

  // update context
  ctx.$solvable = $solvable;
  ctx.data = jqGetElemById('solvable-data').data();
  ctx.solvableDidOnload = ctx.solvable === true;
  ctx.currentState = null;
  ctx.previousState = null;
  solvableContext[solvableId] = ctx;

  comments.add_form();
  comments.votes();

  // don't close sharing dropup when clicking on it
  $solvable.find('.sharing-dd .dropdown-menu').on('click', function (e) {
    e.stopPropagation();
  });

  // close share dropup when clicking share buttons
  $solvable.find('.bsharing-link').on('click', function () {
    $(this).parents('.dropup').removeClass('open');
  });

  $solvable.find('.direct-link').on('click', function () {
    $(this).find('input').select();
  });

  // highlight the solution button if currently viewing solutions
  if (ctx.currentState === 'solutions' || ctx.$solvable.hasClass('solutions')) {
    ctx.currentState = 'solutions';
    ctx.$solvable.find('.solv-details .btn-solution').first().addClass('active');
  }

  zoomableImages($solvable);

  solvable.cropActionsMenu($solvable);

  const $signedUpModal = $('.solv-signed-up');
  if ($signedUpModal.length) {
    // scroll down so that the modal is just above the MCQ/input results
    const modalBottom = $signedUpModal.offset().top + $signedUpModal.outerHeight();
    const solvDetailsTop = ctx.$solvable.find('.solv-details').offset().top;
    setScrollPosition(solvDetailsTop - modalBottom - mediumPadding * 2);
  }
};

/**
 * Return whether there are any solvable editing forms visible
 * @return {boolean}
 */
function areAnySolvableEditFormsVisible() {
  const $choicesEditor = $('.mcq-edit-form');
  const $questionContainer = $('.question-text');
  const $titleEditor = jqGetElemById('solvable_title_editor_container');
  return (
    $choicesEditor.is(':visible') ||
    $questionContainer.hasClass('previewing') ||
    $questionContainer.hasClass('editing') ||
    $titleEditor.hasClass('editing')
  );
}

/**
 * Set up editing title if the user can edit it
 * @param {JQuery} $title The title container
 */
solvable.editableTitle = $title => {
  const $editTitleLink = $title.find('.edit-title-link');
  if ($title !== null && $title.length && $editTitleLink !== null && $editTitleLink.length) {
    const $cancelLink = $title.find('.cancel-edit-link');

    $editTitleLink.on('click', () => {
      if (areAnySolvableEditFormsVisible()) {
        const { errorTitle, errorContent } = $title.data();
        showGenericModal(errorTitle, errorContent);
      } else {
        $title.addClass('editing');
      }
    });

    $cancelLink.on('click', () => {
      $title.removeClass('editing');
    });
  }
};

/**
 * Loads the deferred recently solved by component. Component container is hidden, so the
 * component load must be triggered manually
 * @param {JQuery} $container
 */
solvable.solversInit = function ($container) {
  solvableContext[$container.data('solvable')].$solversContainer = $container;
  if ($container.find('.cmp-deferred').length) {
    loadSolversComponent(findSolvableContext($container));
  } else {
    $container.appendTo(jqGetElemByCmpName($container.data('cmp-target'))).removeClass('hide');
  }
};

/**
 * Controller for answer input form
 */
solvable.answerFormWrapper = function ($formContainer) {
  const $input = $formContainer.find('input#id_answer');

  if ($input.length) {
    const handleSubmitButton = () => {
      const submitDisabled = $input.val().trim() === '';
      $formContainer
        .find('.btn-submit')
        .prop('disabled', submitDisabled)
        .toggleClass('disabled', submitDisabled);
    };

    $input.on('input', handleSubmitButton);

    handleSubmitButton();
  }

  _.defer(() => {
    const ctx = findSolvableContext($formContainer);
    $formContainer.find('form').on('submit', () => {
      ctx.$solvable.find('.spinner').addClass('active');
    });

    if (!isMobile) {
      if (!$formContainer.data('disable-auto-focus')) {
        $formContainer.find('#id_answer').select();
      }

      // android 4.0.x: prevent input field showing up on topic of footer
    } else if (navigator.userAgent.indexOf('Android 4.0') !== -1) {
      $formContainer.find('#id_answer').on('focus', function () {
        var $input = $(this);
        setTimeout(() => {
          const offset = $input.offset().top;
          const newPosition = offset - 20;
          if (newPosition > 0) {
            setScrollPosition(newPosition);
          }
        }, 500);
      });
    }

    scaleMcqSvgs(ctx);
  });
};

/**
 * Controller for logged-out solvable forms, MCQ and text
 * @param {JQuery} $form
 */
solvable.loggedOutForm = function ($form) {
  var $answer = $form.find('.logged-out-answer');
  var $solutionButton = $form
    .parents('.solv-details,.solv-details-loggedout')
    .find('.logged-out-solution');
  var $signupModal = jqGetElemById('solv-public-signup');
  var $signupSidebar = $signupModal.find('#public-signup');
  var $signup_facebook = $signupModal.find('#signup-fb');
  var $login_facebook = $signupModal.find('#login-fb');
  var $signup_google = $signupModal.find('#signup-google');
  var $login_google = $signupModal.find('#login-google');
  var $signup_email = $signupModal.find('#signup-email, #problem-signup-link-alternative');
  var $login_email = $signupModal.find('#problem-login-link, #problem-login-link-alternative');
  var $answerContainer = $signupModal.find('#public-answer');
  var $mcqButtons = $form.find('.btn-mcq');
  var $mcq_answer = $form.find('[name=logged-out-mcq]');
  var isMCQ = $form.hasClass('mcq-form');
  var isMultiSelect = $form.hasClass('multi-select');
  var multiSelectSeparator = '|';
  var data = $form.data();
  var initial_signup_facebook_href = $signup_facebook.attr('href');
  var initial_login_facebook_href = $login_facebook.attr('href');
  var initial_signup_google_href = $signup_google.attr('href');
  var initial_login_google_href = $login_google.attr('href');
  var initial_signup_email_href = $signup_email.attr('href');
  var initial_login_email_href = $login_email.attr('href');

  var getValue = function () {
    var value = '';
    if (isMultiSelect) {
      var answers = [];
      $mcq_answer.each(function (index) {
        if ($(this).is(':checked')) {
          answers.push(index + 1);
        }
      });
      value = answers.join(multiSelectSeparator);
    } else if (!isMCQ && $answer.length) {
      value = $answer.val();
    } else if ($mcq_answer.length) {
      var $mcq_answer_checked = $mcq_answer.filter(':checked');
      value = $mcq_answer_checked.length ? $mcq_answer_checked.val() : '';
    }

    return value.trim();
  };

  var appendEscapedParams = function (str, params) {
    if (!str) {
      return;
    }
    // check for encoded '?'
    var prefix = str.indexOf('%3F') === -1 ? '?' : '&';
    return str + encodeURIComponent(prefix + params);
  };

  var setup_urls_and_tracking = function (e, mpkey) {
    e.preventDefault();
    var value = getValue();
    var social_params = {};
    var email_params = {};
    var signup_facebook_href = initial_signup_facebook_href;
    var login_facebook_href = initial_login_facebook_href;
    var signup_google_href = initial_signup_google_href;
    var login_google_href = initial_login_google_href;
    var signup_email_href = initial_signup_email_href;
    var login_email_href = initial_login_email_href;

    analytics.track('ui', {
      action: 'click',
      element_type: 'button',
      element_id: 'logged_out_solvables_' + mpkey,
    });

    var is_nonnumber_answer = isNaN(parseFloat(value));

    $signupSidebar.removeClass('text-answer alt show-form');

    if (value.length) {
      if (value.indexOf(multiSelectSeparator) >= 0) {
        // multiple answers selected
        $answerContainer.text('Those choices');
      } else {
        // only one answer selected
        if (isMCQ && mathjaxLoader.textContainsUnrenderedMath(value)) {
          var $answerInput = $mcq_answer.filter(function () {
            return this.value === value;
          });
          var answerHTML = $answerInput.parents('.btn-mcq').children('span').html();
          $answerContainer.html(answerHTML);
        } else {
          $answerContainer.text(
            value
              .replace(/__replace_plus__/g, '+')
              .replace(/__rep_perc__/g, '%')
              .replace(/__rep_ques__/g, '?')
              .replace(/__rep_quot__/g, '"'),
          );
        }
      }
      // add value the old way
      social_params['attempt'] = encodeURIComponent(value);
      email_params['attempt'] = encodeURIComponent(value);
      $signup_email.add($login_email).data('attempt', value);
      $signup_email.add($login_email).attr('data-attempt', value);

      if (is_nonnumber_answer) {
        $signupSidebar.addClass('text-answer');
      }
    } else {
      if (!data.interactive) {
        $signupSidebar.addClass('alt');
      }
      value = 'no-input';
    }

    // add value the new way
    if (signup_facebook_href) {
      signup_facebook_href = signup_facebook_href.replace(
        'try-value-value',
        encodeURIComponent(value),
      );
    }
    if (login_facebook_href) {
      login_facebook_href = login_facebook_href.replace(
        'try-value-value',
        encodeURIComponent(value),
      );
    }
    if (signup_google_href) {
      signup_google_href = signup_google_href.replace('try-value-value', encodeURIComponent(value));
    }
    if (login_google_href) {
      login_google_href = login_google_href.replace('try-value-value', encodeURIComponent(value));
    }
    if (signup_email_href) {
      signup_email_href = signup_email_href.replace('try-value-value', encodeURIComponent(value));
    }
    if (login_email_href) {
      login_email_href = login_email_href.replace('try-value-value', encodeURIComponent(value));
    }

    if (data.isQuiz || data.isDailyProblem) {
      // no problem slug for DC
      const problemParam = data.solvableSlug ? `problem=${data.solvableSlug}` : null;
      const attemptParam = `attempt=${value}`;
      const paramStr = problemParam ? `${problemParam}&${attemptParam}` : attemptParam;
      signup_facebook_href = appendEscapedParams(signup_facebook_href, paramStr);
      login_facebook_href = appendEscapedParams(login_facebook_href, paramStr);
      signup_google_href = appendEscapedParams(signup_google_href, paramStr);
      login_google_href = appendEscapedParams(login_google_href, paramStr);
      signup_email_href = appendEscapedParams(signup_email_href, paramStr);

      var loginParamStr = '';
      if (login_email_href.indexOf('problem=') == -1) {
        loginParamStr += 'problem=' + data.solvableSlug;
        if (login_email_href.indexOf('attempt=') == -1) {
          loginParamStr += '&';
        }
      }
      if (login_email_href.indexOf('attempt=') == -1) {
        loginParamStr += 'attempt=' + encodeURIComponent(value);
      }
      login_email_href = appendEscapedParams(login_email_href, loginParamStr);
    }

    if (data.topicTag) {
      social_params['topic_tag'] = data.topicTag;
      email_params['topic_tag'] = data.topicTag;
      signup_facebook_href = appendEscapedParams(
        signup_facebook_href,
        'topic_tag=' + data.topicTag,
      );
      login_facebook_href = appendEscapedParams(login_facebook_href, 'topic_tag=' + data.topicTag);
      signup_google_href = appendEscapedParams(signup_google_href, 'topic_tag=' + data.topicTag);
      login_google_href = appendEscapedParams(login_google_href, 'topic_tag=' + data.topicTag);
      $signup_email.add($login_email).data('topic_tag', data.topicTag);
      $signup_email.add($login_email).attr('data-topic_tag', data.topicTag);
    }

    if (signup_email_href) {
      var signupNext = signup_email_href.match(/next=(.*)/);
      if (signupNext.length) {
        $signup_email.data('next', signupNext[1]);
        $signup_email.attr('data-next', signupNext[1]);

        $signup_email.data('already_escaped', true);
        $signup_email.attr('data-already_escaped', true);
      }
    }
    if (login_email_href) {
      var loginNext = login_email_href.match(/next=(.*)/);
      if (loginNext.length) {
        var loginNextStr = decodeURIComponent(loginNext[1]);
        $login_email.data('next', loginNextStr);
        $login_email.attr('data-next', loginNextStr);
      }
    }

    $signup_facebook.attr('href', urls.appendGetParams(signup_facebook_href, social_params));
    $login_facebook.attr('href', urls.appendGetParams(login_facebook_href, social_params));
    $signup_google.attr('href', urls.appendGetParams(signup_google_href, social_params));
    $login_google.attr('href', urls.appendGetParams(login_google_href, social_params));
    $signup_email.attr('href', urls.appendGetParams(signup_email_href, email_params));
    $login_email.attr('href', urls.appendGetParams(login_email_href, email_params));
  };

  var show_signup_opts = function (e, mpkey) {
    if ($body.hasClass('hundredday-page')) {
      const $solvable = jqGetElemById('solv-main');
      $solvable.css({
        height: $solvable.outerHeight(),
      });
      $signupModal.appendTo($body);

      jqOffOn($signupModal.find('.modal-bg'), 'click.modalhack', () => {
        $solvable.css('height', 'auto');
        $signupModal.insertAfter('.solv-problem');
      });
    }

    setup_urls_and_tracking(e, mpkey);

    jqGetElemById('nf-modal-close').hide();
    $signupModal.addClass('open');
    latex.scaleSVG($answerContainer);

    $answer.blur();

    const $signupLoginModalTour = $signupModal.find('#public-signup-tour');
    if ($signupLoginModalTour.length && !$signupLoginModalTour.data('already-opened')) {
      $signupLoginModalTour.data('already-opened', true);
      $signupLoginModalTour.attr('data-already-opened', true);

      analytics.track('ui', {
        action: 'viewed_prenux_slide',
        step_position: 1,
        is_first_step: true,
        is_last_step: false,
      });
    }

    $signupModal.on('click', function () {
      $signupModal.removeClass('open');
    });
  };

  $form.on('submit', function (e) {
    e.preventDefault();
    show_signup_opts(e, 'submitted_form');
  });

  jqOffOn($solutionButton, 'click', e => {
    $answer.val('');
    $mcqButtons.removeClass('active dim');
    $mcq_answer.filter(':checked').prop('checked', false);

    show_signup_opts(e, 'clicked_solution_btn');
  });

  // un-disable other solution buttons
  $body.on(EVT_QUIZ_POSITION_DID_CHANGE, () => {
    $('.logged-out-solution').removeClass('dim disabled').removeAttr('disabled');
  });
};

solvable.preventSocialButtonDoubleClick = function ($container) {
  $container.find('.signup-social').on('click', function (e) {
    var $this = $(this);
    if ($this.hasClass('disabled')) {
      e.preventDefault();
    } else {
      $this.addClass('disabled');
      setTimeout(function () {
        $this.removeClass('disabled');
      }, 500);
    }
  });
};

/**
 * Controller for single_select form
 * @param {JQuery} $form
 */
solvable.mcqForm = function ($form) {
  var $selectors = $form.find('label');

  var handleSubmitButton = function () {
    var submitDisabled = $form.find('input:checked').length <= 0;
    $form
      .find('.btn-submit')
      .prop('disabled', submitDisabled)
      .toggleClass('disabled', submitDisabled);
  };

  $selectors.on('click', function () {
    var $this = $(this);
    var $input = $this.find('input');
    var val = $input.val() || null;

    if (val !== null) {
      $input.prop('checked', true);
      $selectors.removeClass('active dim');
      $this.addClass('active');
      clickedChoice = val.replace(/__replace_plus__/g, '+');
    }

    handleSubmitButton();
  });

  handleSubmitButton();
};

/**
 * Controller for multi_select form
 * @param {JQuery} $form
 */
solvable.multiSelectForm = function ($form) {
  var handleSubmitButton = function () {
    var submitDisabled = $form.find('input:checked').length <= 0;
    $form
      .find('.btn-submit')
      .prop('disabled', submitDisabled)
      .toggleClass('disabled', submitDisabled);
  };

  $form.find('label').on('click', function (e) {
    e.preventDefault();

    var $this = $(this);

    setTimeout(function () {
      var $input = $this.find('input');
      var val = $input.val() || null;
      var previouslyChecked = $input.is(':checked');

      if (val !== null) {
        if (previouslyChecked) {
          $input.prop('checked', false);
          $this.removeClass('active');
        } else {
          $input.prop('checked', true);
          $this.addClass('active');
        }

        handleSubmitButton();
      }
    });
  });

  handleSubmitButton();
};

/**
 * Attempt to resend the answer if the connection dies
 * @param {JQuery} $form
 */
solvable.answerForm = function ($form) {
  var $input = $form.find('#id_answer');
  $form.on('submit', function (e) {
    e.preventDefault();

    var formId = $form.attr('id'),
      isMCQ = formId === 'mcq-form',
      isNumber = formId === 'text_answer_entry_form',
      answer = $input.val(),
      placeholder = $input.data('placeholder');

    // in logged out practice, if the user has already answered a certain
    // number of problems, show the sign up modal unless an empty form is posted
    if (
      isLoggedOutPractice &&
      problemsLeftBeforeSignup === 0 &&
      (isMCQ || (isNumber && answer !== '' && answer !== placeholder))
    ) {
      jqGetElemById('solv-public-form').addClass('open');
      return;
    }

    // get a new instance of ajax to override the error handler
    const formAjax = ajax($form.find('#id_page_key').val());

    var $solvable = $form.parents('.solv-main'),
      $originalSidebar = $form.parents('.solv-details').clone(),
      trackedResend = false,
      resendCount = 0,
      // try resending every 500 ms
      resendFrequency = 500,
      // attempt to resend up to 120 times (1 minute)
      resendLimit = 120;

    var trackResend = function (wasSuccessful) {
      if (resendCount && !trackedResend) {
        trackedResend = true;
        analytics.track('ui', {
          element_id: 'solvable_answer_resend',
          resend_count: resendCount,
          resend_successful: wasSuccessful,
          resend_total_time: resendCount * resendFrequency,
        });
      }
    };

    // try to resend the answer if we haven't reached the resend limit
    const resendAnswer = data => {
      if (resendCount < resendLimit) {
        resendCount++;
        setTimeout(() => {
          formAjax.post(data.url, data);
        }, resendFrequency);
      } else {
        showGenericModal('Error', $form.data('max-resend-error'));
        revertForm();
        trackResend(false);
      }
    };

    // revert the form when all else fails
    var revertForm = function () {
      var $sidebar = $solvable.find('.solv-details');
      $sidebar.replaceWith($originalSidebar);

      $solvable.find('.b2-solution').hide();
      $solvable.removeClass('solutions');

      $originalSidebar.find('.spinner').removeClass('active');
      $originalSidebar.find(':disabled,.dim').removeClass('dim disabled').removeAttr('disabled');

      initGenericControllers($originalSidebar);
    };

    // override the success function to track any resends that happened
    const oldSuccessHandler = formAjax.success;
    formAjax.success = function () {
      // super()
      oldSuccessHandler.apply(formAjax, arguments);

      trackResend(true);
    };

    // handle errors
    formAjax.error = function (xhr, data) {
      // no connection, try to resend
      if (xhr.status === 0) {
        resendAnswer(data);

        // timeout, show timeout error and revert form
      } else if (xhr.statusText === 'timeout') {
        showTimeoutErrorModal();
        revertForm();

        // 500, show default error and revert form
      } else if (xhr.status > 0) {
        errorModalHandler(xhr);
        revertForm();
      }
    };

    // _.defer is to make sure that instant response data is ready by the moment
    // the answer is going to be sent
    _.defer(function ($formClone) {
      var irData = $solvable.data('ir-verification'),
        // extra data to be sent to the server to catch mismatches between the instant
        // response and the "true" answer from the server
        extra_data = {};

      // sometimes (super rarely), IR data is not ready by this moment
      try {
        if (irData['preventUpdate']) {
          extra_data['instant_response_check'] = 'cant_tell';
        } else {
          extra_data['instant_response_check'] = irData['answerIsCorrect']
            ? 'correct'
            : 'incorrect';
        }
        extra_data['instant_response_problem_completed'] = irData['canStillAnswer'] ? 'no' : 'yes';
        extra_data['instant_response_tries_left'] = irData['triesLeft'];
      } catch (e) {}

      // send the form (the first time)
      formAjax.form($formClone, extra_data);
    }, $form.clone());
  });
};

/**
 * Handle form events from the interactive.
 * @param {JQuery} $solvable
 */
solvable.interactiveSolvable = $solvable => {
  const prefix = `InteractiveProblem.${$solvable.data('solvable')}`;
  const $getSolvableForm = () =>
    userIsAuthenticated
      ? $solvable.find('#solv-interactive-form')
      : $solvable.find('.solv-form-logged-out');

  jqOffOn($window, `${prefix}.export-state`, (e, data) => {
    $solvable.find('[name=current_state]').val(JSON.stringify(data.state));
  });

  jqOffOn($window, `${prefix}.check-answer`, (e, { isSolved }) => {
    if (userIsAuthenticated) {
      $solvable.find('[name=mark_correct]').prop('checked', isSolved === true);
      $getSolvableForm().submit();
    }
  });

  jqOffOn($window, `${prefix}.show-signup-modal`, (e, { solvableType }) => {
    if (!userIsAuthenticated) {
      const $form = $getSolvableForm();
      $form.find('[name=answered]').prop('checked', true);
      const $signupSidebar = jqGetElemById('solv-public-signup').find('.public-signup-right');
      if (solvableType === 'auto-check') {
        $signupSidebar.addClass('autochecked-interactive');
      } else {
        $signupSidebar.addClass('alt');
      }
      $form.submit();
    }
  });

  jqOffOn($window, `${prefix}.show-explanation`, () => {
    if (userIsAuthenticated) {
      $('.btn-solution-full').trigger($.Event('click'));
    } else {
      const $form = $getSolvableForm();
      $form.find('[name=answered]').prop('checked', false);
      $form.submit();
    }
  });

  jqOffOn($window, `${prefix}.continue`, () => {
    const query = userIsAuthenticated ? '.solv-done-btn' : '.next-marker-btn';
    $solvable.find(query).trigger($.Event('click'));
  });
};

/**
 * Controller for edit problem form
 * @param {JQuery} $container
 */
solvable.edit = function ($container) {
  const $editForm = $container.find('form');
  const $ownEditBtns = $container.find('.btn-edit');
  const $outerEditBtns = $('.solv-edit-trigger').not('.btn-edit');
  let $context = $container.closest('.course-quiz-flex');
  if ($context.length === 0) {
    $context = $container.closest('.solv-main');
  }
  let $editMcqBtn = $context.find('.solvable-mcq-options-btn');
  const $choicesEditor = $context.find('.mcq-edit-form');
  const $cancelButton = $context.find('.edit-mcq-cancel');
  const $previewBtn = $container.find('.question-text-preview');
  const $saveBtn = $container.find('.btn-save');
  const $cancelSolvableEditButton = $container.find('.cancel-edit');
  const $previewToggleField = $container.find('.question-text-preview-field');
  const $solutionBtn = $container.find('.show-solution');
  const $textarea = $editForm.find('textarea').not('.codex-textarea textarea');
  const editingClass = 'editing';
  const previewingClass = 'previewing';
  const isPreview = $container.hasClass(previewingClass);

  if (!$editMcqBtn.length) {
    $editMcqBtn = jqGetElemById('answer-edit-trigger');
  }

  /**
   * Whether the form actually saves changes or just returns a preview is controlled with
   * a boolean form field which sometimes needs to be adjusted manually before submitting,
   * along with a respective data attribute of the container.
   * @param {Boolean} isPreviewMode - true if the form needs to return a preview first
   */
  const togglePreviewMode = isPreviewMode => {
    $previewToggleField.val(isPreviewMode.toString());
    $container.data('previewing', !isPreviewMode);
  };

  const editingHandler = function (e) {
    const $trigger = e ? $(e.currentTarget) : null;
    if (e) {
      e.preventDefault();
    }
    if ($trigger && $trigger.is(':not(.is-reedit-trigger)') && areAnySolvableEditFormsVisible()) {
      const { errorTitle, errorContent } = $container.data();
      showGenericModal(errorTitle, errorContent);
    } else {
      if ($trigger) {
        $trigger.addClass(CLASS_NAME_ACTIVE);
      }
      $container.removeClass(previewingClass);
      $container.addClass(editingClass);
      $(window).scrollTop($editForm.offset().top - 150);
      autosize($textarea);
      togglePreviewMode(true);
    }
  };

  $cancelSolvableEditButton.on('click', e => {
    if (e) {
      e.preventDefault();
    }

    if (isPreview) {
      const data = $container.data();
      showConfirmationModal({ title: data.cancelConfirmText }).then(confirmed => {
        if (confirmed) {
          // reload solvable component to revert changes
          const { cmpUrl, cmpKey, pageKey, paramKey } = data;
          ajax(pageKey).get(cmpUrl, {
            component_key: cmpKey,
            param_key: paramKey,
          });
        }
      });
    } else {
      $container.removeClass(editingClass);
      togglePreviewMode(false);
      $ownEditBtns.add($outerEditBtns).removeClass(CLASS_NAME_ACTIVE);
    }
  });

  $ownEditBtns.on('click', editingHandler);
  jqOffOn($outerEditBtns, 'click', editingHandler);
  $container.on('edit', editingHandler);

  if ($container.hasClass(editingClass)) {
    editingHandler();
  }

  $saveBtn.on('click', e => {
    const isSaveMode = $previewToggleField.val() === 'false';
    if (!isSaveMode) {
      const data = $container.data();
      e.preventDefault();
      showConfirmationModal({ title: data.saveConfirmText }).then(confirmed => {
        if (confirmed) {
          togglePreviewMode(false);
          $saveBtn.parents('form').trigger('submit');
        }
      });
    }
  });

  $editMcqBtn.on('click', e => {
    e.preventDefault();

    if (areAnySolvableEditFormsVisible()) {
      const { errorTitle, errorContent } = $container.data();
      showGenericModal(errorTitle, errorContent);
    } else {
      $choicesEditor.siblings().hide();
      $choicesEditor.show();
    }
  });

  $cancelButton.on('click', e => {
    e.preventDefault();
    $choicesEditor.siblings(':not(script)').show();
    $choicesEditor.hide();
  });

  $previewBtn.on('click', e => {
    e.preventDefault();
    $editForm.submit();
  });

  // trigger spinner when saving
  $editForm.on('submit', () => {
    const data = $container.data();
    if (data.suspended && data.previewing) {
      showGenericModal(data.suspendedTitle, data.suspendedText);
    }
  });

  $solutionBtn.on('click', e => {
    const $button = $(e.target);
    e.preventDefault();
    $button.next().toggleClass('hide');
    const sovableId = $textarea[0].classList[0].replace('solvable_question-', '');
    const analyticsData = {
      action: 'staff_only_action',
      solvable: sovableId,
    };
    if ($button.html() === 'Show Solution') {
      $button.html('Hide Solution');
      analyticsData.element_id = 'clicked_show_solution';
    } else {
      $button.html('Show Solution');
      analyticsData.element_id = 'clicked_hide_solution';
    }
    analytics.track('ui', analyticsData);
  });

  $editForm
    .find('.viz-wrapper')
    .on('click', 'button:not([type]), button[type=submit], input[type=submit]', e =>
      e.preventDefault(),
    );
};

/**
 * Solution button controller
 * @param {JQuery} $btn
 */
solvable.solutionButton = function ($btn) {
  var btnData = $btn.data(),
    ctx = findSolvableContext($btn);

  // handle clicks
  jqOffOn(
    $btn,
    'click.showSolution',
    _.debounce(e => {
      e.preventDefault();

      // do nothing if this button is disabled
      if ($btn.hasClass('disabled')) {
        return;
      }

      const $solutions = $('.solv-solutions');

      // scroll to solutions
      if (isMobile && $solutions.hasClass('expanded')) {
        setScrollPosition($solutions.offset().top - $('.m-action-bar').height());
      }

      // use $btn.data here instead of btnData in case the $btn has been updated
      // since initialization -- this happens when the button is outside of the component
      if ($btn.data('solutions-loaded')) {
        handlePassiveSolutionButton(ctx);
      } else {
        const handleButton = () => {
          const $btnContent = isMobile ? $btn.find('.main') : $btn;
          $btn.addClass('dim').blur();
          toggleButtonLoadingState($btnContent, true);
          handleActiveSolutionButton(btnData);
        };

        // confirmation box is available for mobile solution discussions if
        // not attempt.completed()
        const $confirmBox = jqGetElemById('solv-confirm-box');
        if ($confirmBox.length) {
          if (isMobile) {
            const confirmClass = 'active';

            $body.append($confirmBox);
            $confirmBox.show();
            _.defer(() => {
              $confirmBox.addClass(confirmClass);
            });

            jqOffOn($confirmBox.find('.btn'), 'click', event => {
              $confirmBox.removeClass(confirmClass);
              setTimeout(() => {
                $confirmBox.hide();
              }, 210);

              if ($(event.currentTarget).hasClass('btn-confirm')) {
                handleButton();
              }
            });
          } else {
            handleButton();
          }
        } else {
          handleButton();
        }
      }
    }, 300),
  );

  // check if the user is trying to view a solution
  if (
    window.location.href.indexOf('/solution-comments/') !== -1 &&
    ctx.currentState !== 'solutions'
  ) {
    if ($btn.data('solutions-loaded')) {
      handlePassiveSolutionButton(ctx);
    } else if (ctx.$solvable.find('.solv-details.endstate').length) {
      handleActiveSolutionButton($btn.data());
    }
  }
};

/**
 * Dispute discussions trigger controller
 * @param {JQuery} $trigger
 */
solvable.disputeDiscTrigger = function ($trigger) {
  var data = $trigger.data(),
    ctx = findSolvableContext($trigger),
    sent_email_verification = false,
    $modal = $('.dispute-disc-modal');

  $trigger.on('click', function (e) {
    e.preventDefault();
    if (data['disputesLoaded']) {
      setSolvableState('disputes', ctx);
      if (sent_email_verification === false) {
        sent_email_verification = true;
        $('#solvable-resend-verification-form').submit();
      }
    } else {
      $modal.modal('show');
      if (sent_email_verification === false) {
        sent_email_verification = true;
        $modal.find('#solvable-resend-verification-form').submit();
      }
    }
  });
};

/**
 * Set make sure disputes are shown when components load
 * @param {JQuery} $form
 */
solvable.staffDisputeDiscForm = function ($form) {
  $form.on('submit', function () {
    solvableContext[$form.data('solvable')].disputesJustLoaded = true;
  });
};

/**
 * Controller
 * @param {JQuery} $form
 */
solvable.disputeModalForm = $form => {
  const $explanationForm = $form.find('.explanation');
  $form.find('input[type="radio"]').on('change', () => {
    if (!$explanationForm.hasClass('visible')) {
      $explanationForm.addClass('visible');
      autosize($explanationForm.find('textarea'));
    }
  });
};

/**
 * Controller
 * @param {JQuery} $element
 */
solvable.disputeModalFormEndState = $element => {
  $element.find('.view').on('click', () => {
    $element.find('.view-reports').addClass('loading');
    jqGetElemById('enter-dispute-discussions-form').trigger('submit');
  });
};

/**
 * Controller.
 * @param {JQuery} $trigger
 */
solvable.revealDisputesModal = $trigger => {
  $trigger.on('click', () => {
    $('.reveal-disputes-modal').clone(true).modal('show');
  });
};

/**
 * Controller.
 * @param {JQuery} $trigger
 */
solvable.revealDisputes = $trigger => {
  $trigger.on('click', e => {
    $(e.currentTarget).addClass(`${CLASS_NAME_DISABLED} ${CLASS_NAME_LOADING}`);
    jqGetElemById('enter-dispute-discussions-form').trigger('submit');
  });
};

/**
 * Controller.
 * @param {JQuery} $form
 */
solvable.enterDisputeDiscussionsForm = $form => {
  $form.on('submit', () => {
    solvableContext[$form.data('solvable-id')].disputesJustLoaded = true;
  });
};

/**
 * Controller for problem deletion form
 * @param {JQuery} $deleteFormContainer
 */
solvable.deleteForm = $deleteFormContainer => {
  const $deleteBtn = jqGetElemById('stats-delete-btn');

  // don't bother with anything until the user has clicked the button
  $deleteBtn.on('click', () => {
    const $deleteForm = $deleteFormContainer.find('form');

    // create the modal
    $deleteFormContainer.modal();

    // hide the button
    $deleteBtn.addClass('hide');

    // hide the modal when submitting the form
    $deleteForm.on('submit', () => {
      $deleteFormContainer.modal('hide');
    });
  });
};

/**
 * Moves the most popular recommended problem from the solvable footer to this container.
 * We do it this way to avoid the second call to `{% recommend_solvables ... %}` which
 * would be inefficient.
 */
solvable.mostPopularRecommendedProblem = function ($container) {
  var $recommendedProblem = $('.recommended-problems .most-popular-recommended-problem');
  if ($recommendedProblem.length) {
    $recommendedProblem.clone(true, true).appendTo($container.empty());
  }
};

/**
 * Crop actions menu
 * @param {JQuery} $solvable
 */
solvable.cropActionsMenu = $solvable => {
  const $solvableActions = $solvable.find('.solv-actions-dropdown');
  $solvableActions.find('.dropdown-toggle').on('click', () => {
    let maxHeight = $solvable.outerHeight(true) - $solvable.find('.solv-footer').outerHeight(true);

    if (isMobile) {
      maxHeight -= $solvable.find('.solv-details').outerHeight(true);
    }

    $solvableActions.find('.dropdown-menu').css('max-height', maxHeight);
  });
};

/**
 * Show/hide the chapter intro in mobile quizzes
 * @param {JQuery} $container
 */
solvable.showChapterIntro = function ($container) {
  var $quizWrapper = $container;

  if (!$container.hasClass('quiz-wrapper')) {
    $quizWrapper = $container.parents('.quiz-wrapper');

    $container.find('.btn-link').on('click', function () {
      $quizWrapper.toggleClass('chapter-intro-view');
    });
  }

  var $chapterIntro = $quizWrapper.find('.chapter-intro-wrapper'),
    $filterBtns = $chapterIntro.find('.btn-tabs .btn');

  $filterBtns.on('click', function () {
    var $this = $(this);
    if (!$this.hasClass('active')) {
      $filterBtns.removeClass('active');
      $this.addClass('active');
      $chapterIntro.removeClass('intro quizzes wikis').addClass($this.data('value'));
    }
  });
};

/**
 * Controller
 * @param {JQuery} $container
 */
solvable.practiceAnonWrapper = function ($container) {
  isLoggedOutPractice = true;
  problemsLeftBeforeSignup = $container.data('signup_after_n_problems');

  const prevBtnQuery = '.prev-marker-btn';
  const nextBtnQuery = '.next-marker-btn';
  const $problems = $container.find('.prac-anon-item');
  const $navButtons = $container.find(`${prevBtnQuery}, ${nextBtnQuery}`);
  const $markerContainer = $container.find('.prac-markers');
  const $navMarkers = $markerContainer.find('.prac-marker');
  const visibleClass = 'visible';
  const currentClass = 'current';
  const hasMultipleMarkerContainers = $markerContainer.length > 1;
  const numProblems = $problems.length;

  function setNavButtonState(index) {
    $navButtons.filter(prevBtnQuery).toggleClass('disabled', index === 0);
    $navButtons.filter(nextBtnQuery).toggleClass('disabled', index === numProblems - 1);
  }

  function showProblem(index) {
    if (index < 0 || index >= numProblems) {
      return;
    }

    setNavButtonState(index);

    $problems.removeClass(visibleClass).eq(index).addClass(visibleClass);
    if (hasMultipleMarkerContainers) {
      $navMarkers.removeClass(currentClass);
      $($markerContainer[index]).find('.prac-marker').eq(index).addClass(currentClass);
    } else {
      $navMarkers.removeClass(currentClass).eq(index).addClass(currentClass);
    }
    $body.trigger(EVT_QUIZ_POSITION_DID_CHANGE);

    const $codexContainer = $('.prac-anon-item.visible .codex-container');
    if ($codexContainer.length) {
      codexController($codexContainer);
    }
  }

  // Handle clicking left/right nav buttons
  $navButtons.on('click', function (e) {
    e.preventDefault();

    const $btn = $(this);
    const $visibleProblem = $problems.filter('.' + visibleClass);
    let currentProblemIndex = $problems.index($visibleProblem);

    if ($btn.hasClass('prev-marker-btn')) {
      currentProblemIndex--;
    } else if ($btn.hasClass('next-marker-btn')) {
      currentProblemIndex++;
    }

    showProblem(currentProblemIndex);
  });

  // Handle clicking nav markers
  $navMarkers.on('click', function () {
    const $marker = $(this);
    showProblem($marker.index());
  });

  // Add keyboard nav for new quiz UI
  if (hasMultipleMarkerContainers) {
    var numItems = $problems.length;

    const problemSwitcher = adjustment => {
      return () => {
        const currentProblemIndex = $navMarkers.filter('.current').index();
        showProblem(currentProblemIndex + adjustment);
      };
    };

    keyboardNavigation({
      left: problemSwitcher(-1),
      right: problemSwitcher(1),
    });
  }

  setNavButtonState($navMarkers.filter('.current').index());
};

/**
 * Controller.
 * @param {JQuery} $solvable
 */
solvable.practiceAnon = $solvable => {
  if ($solvable.hasClass('prac-anon-item')) {
    const $sidebar = $solvable.find('.solv-details').not('.course-quiz-solv-details');
    const solvableHeight = $solvable.outerHeight();

    // set overflow:auto to calculate scrollHeight
    $sidebar.css({ overflow: 'auto' });

    const detailsHeight = getScrollHeight($sidebar) + 2;

    // change back to overflow:hidden to avoid scrollbars
    $sidebar.css({ overflow: 'hidden' });

    if (solvableHeight < detailsHeight) {
      _.defer(() => {
        $sidebar.css({ position: 'static' });
      });
    }
  }
};

solvable.updateStreaks = function () {
  window.dispatchEvent(new Event('updatestreaks'));
};

solvable.nextSkillB2Modal = (function () {
  let $modal = null;

  return function ($btn) {
    $btn.on('click', e => {
      e.preventDefault();
      if ($modal === null) {
        $modal = jqGetElemById('prac-next-skill-b2').modal();
      } else {
        $modal.modal('show');
      }
    });
  };
})();

solvable.checkAnswerButton = function ($btn) {
  var $solvable = findSolvableContext($btn).$solvable;

  var toggleButtonState = function (disable) {
    disable = disable === true;

    if (isIos && appVersion.isGreaterThanOrEqualTo('0.9.7')) {
      if (disable) {
        window.location = 'disableanswerbutton://';
      } else {
        window.location = 'enableanswerbutton://';
      }
    }

    var disablingClasses = 'btn-outline disabled',
      enablingClasses = 'btn-accent';
    $btn.removeClass(disable ? enablingClasses : disablingClasses);
    $btn.addClass(disable ? disablingClasses : enablingClasses);
  };

  _.defer(function () {
    var $mcqs = $solvable.find('.btn-mcq');
    // enable mcq form on load
    $solvable.find('.mcq-form').removeClass('disable-for-mobile');
    $mcqs.one('click', toggleButtonState);
  });

  $btn.on('click', function () {
    if (!$btn.hasClass('disabled')) {
      $solvable.find('#mcq-form').submit();
    }
  });
};

/**
 * Recent solvers component controller
 * @param {JQuery} $solversContainer
 */
solvable.recentSolvers = function ($solversContainer) {
  // cache the component to prevent multiple reloads
  solvableContext[$solversContainer.data('solvable')].$solversList = $solversContainer;

  // add left padding to likes
  var $likes = $solversContainer.parents('.solv-author').find('.likes-container');
  var solversWidth = $solversContainer.find('.btn-expand').width();
  $likes.css('padding-left', solversWidth + 14);
  $solversContainer.removeClass('hidden');

  jqOffOn($solversContainer.find('button'), 'click', () => {
    $solversContainer.toggleClass('expanded');

    // add left padding to likes
    const newSolversWidth = $solversContainer.find('button:visible').width();
    $likes.css('padding-left', newSolversWidth + 14);
  });
};

// vkeys
// --------------------------------------------------

/**
 * Modal solvable vkey
 * @param {JQuery} $root
 */
vkey.assessment_modal_public_solvable_component = function ($root) {
  var $solvable = $root.find('.solv-main'),
    solvableId = $solvable.data('solvable');

  // reset onload
  solvableContext[solvableId] = solvableContext[solvableId] || {};
  solvableContext[solvableId].solvableDidOnload = false;

  initGenericControllers($root);
  comments.add_form();
  comments.votes();
  comments.events();

  $root.find('.modal-target').each(contentModal.modal_events);

  initAutosubmit($root);
};
solvable.assessment_modal_public_solvable_component =
  vkey.assessment_modal_public_solvable_component;

/**
 * Hide/show staff bios and hover actions for toggle buttons
 * @param {JQuery} $authorSection
 */
solvable.staffBios = function ($authorSection) {
  const timeoutDelay = $authorSection.data('delay') != null ? $authorSection.data('delay') : 200;
  const $bios = jqGetElemById('prac-staff-bios');
  let showBios = false;

  var toggleHover = function (e) {
    if (!$authorSection.data('flag')) {
      $authorSection.toggleClass('bios-hover');
    }
  };

  const toggleBios = e => {
    if (isMobile) {
      $bios.modal();
    } else {
      e.stopPropagation();

      const flag = $authorSection.data('flag');
      if (flag) {
        flags.update(flag, true);
        $authorSection.removeData('flag');
        $authorSection.removeAttr('data-flag');
      }

      showBios = !showBios;
      $authorSection.toggleClass('bios-visible', showBios);
      if (showBios) {
        $bios.show();
        _.defer(() => {
          $bios.addClass('in');
        });
      } else {
        $bios.removeClass('in');
        setTimeout(() => {
          $bios.hide();
        }, timeoutDelay);
      }
    }
  };

  $authorSection
    .find('.bios-hover-trigger')
    .on('mouseenter mouseleave', toggleHover)
    .on('click', toggleBios);

  $authorSection.find('.btn-bios').on('click', toggleBios);
};

/**
 * Button to toggle B2 solution
 * @param {JQuery} $solutionButton
 */
solvable.pracSolutionToggle = (function () {
  var trackedLockedSolution = {};
  return function ($solutionButton) {
    $solutionButton.on('click', function () {
      handlePassiveSolutionButton(findSolvableContext($solutionButton));
      $solutionButton.blur();

      // extra tracking for b2 solution toggle button
      var data = $solutionButton.data();
      if (data.axUrl && data.targetObjectId && !trackedLockedSolution[data.targetObjectId]) {
        var params = {
          element_id: data.axId,
          source_attempt_id: data.sourceAttemptId,
          target_object_id: data.targetObjectId,
          target_object_type: 'solution',
          target_object_referrer: encodeURIComponent(data.targetObjectReferrer),
        };
        $.get(data.axUrl, params);
        trackedLockedSolution[data.targetObjectId] = true;
      }
    });
  };
})();

/**
 * Controller to stick endstate buttons
 */
solvable.endstate = $btns => {
  const $container = $btns.closest('.solv-main');
  const $footer = $container.find('.solv-author');
  const $problem = $container.find('.solv-problem');

  if (!$footer.length) {
    return;
  }

  _.defer(() => {
    const footerOffsetBottom = $footer.offset().top;
    let problemHeight = $problem.outerHeight(true);
    const detailsHeight = $container.find('.solv-details').outerHeight(true);

    if (problemHeight < detailsHeight) {
      const topMargin = parseInt($problem.css('margin-top'), 10);
      problemHeight = detailsHeight;
      if (topMargin < 0) {
        problemHeight -= topMargin;
      }
      $problem.css('min-height', problemHeight);
    }

    let heightMethod = 'height';
    if ($btns.find('.potw-solutions-activity').length) {
      heightMethod = 'outerHeight';
    }

    sticky.stick($btns, {
      bottom: footerOffsetBottom,
      handler($sticky) {
        // handler for more graceful un-stuck-ing
        if ($sticky.hasClass('stuck-bottom')) {
          // using outerHeights instead of offset because $sticky is
          // absolute relative to parent not document
          const absoluteTop = Math.max(problemHeight, detailsHeight) - $sticky[heightMethod]();
          $sticky.css('top', absoluteTop);
        } else {
          $sticky.css('top', '');
        }
      },
      namespace: 'sticky-solv-details-endstate',
    });
  });
};

/**
 * Show solutions after creating a problem
 * @param {JQuery} $solvableContainer
 */
solvable.autoshowSolutions = function ($solvableContainer) {
  var ctx = findSolvableContext($solvableContainer);
  _.defer(function () {
    ctx.$solvable.find('.btn-solution[data-controller*=solutionButton]').click();

    // strip just_created=true so we don't keep opening solutions every time the user refreshes
    if ('replaceState' in window.history) {
      window.history.replaceState(
        {},
        '',
        window.location.href.replace(/[\?|&]just_created=true/, ''),
      );
    }
  });
};

/**
 * Controller.
 * Modal for likely wiki contributors.
 * @param {JQuery} $modal
 */
solvable.wikiContribModal = $modal => {
  jqGetElemById('wiki-contrib-btn').on('click', () => {
    $modal.modal();
  });
};

/**
 * Controller.
 * @param {JQuery} $link
 */
solvable.previewCustomEndstateText = $link => {
  $link.on('click', () => {
    jqGetElemById('solv-custom-endstate-preview').toggle();
  });
};

/**
 * Show embedded wiki/note problems in modal
 * @param {JQuery} $container
 */
solvable.wikiModalProblem = $container => {
  contentModal.feeditem_modal();
  $container.find('.load-modal').each(function () {
    if (isNative) {
      const $link = $(this).find('.modal-link');
      let href = $link.attr('href');

      href += href.indexOf('?') === -1 ? '?' : '&';
      href += 'newwindow=true';
      $link.attr('href', href).attr('target', '');
    } else {
      contentModal.modal_events.apply(this, arguments);
      latex.checkOverflow($(this));
    }
  });
};

/**
 * Expands/collapses embedded wiki problems
 * @param {JQuery} $problem
 */
solvable.wikiExpandableProblem = function ($problem) {
  var $problemContent = $problem.find('.problem-container');
  $problem.on('click', function (e) {
    // was :before clicked?
    if (e.pageY < $problemContent.offset().top) {
      $problem.toggleClass('expanded');
    }
  });
};

/**
 * handles interactive and animation parts of the solution feedback form
 * @param {JQuery} $container
 */
solvable.sendFeedback = $container => {
  const $textarea = $container.find('textarea');
  const $sendButton = $container.find('.btn-accent');
  const $likeButtons = $container.find('.btn-feedback');
  const $superContainer = $container.closest('.no-stars');
  const $quizStats = $superContainer.find('#quiz_stats_hider');
  const $nextQuiz = $superContainer.parent().find('.btn-accent.btn.next');
  let thumbs;

  const showStats = () => {
    if ($quizStats.length > 0 && $superContainer.find('.feedback-smiley').length > 0) {
      $quizStats.removeClass('hide');
      $nextQuiz.html('Next quiz');
    }
  };
  // enable submit when textarea is filled in
  jqOffOn($textarea, 'keyup', e => {
    if ($textarea.val()) {
      $sendButton.removeClass('disabled');
    } else {
      $sendButton.addClass('disabled');
    }
  });
  // start css animation on click
  jqOffOn($likeButtons, 'click', e => {
    $(e.currentTarget).toggleClass('active');
    thumbs = e.currentTarget.value;
  });

  const feedbackAjax = ajax($container.find('#id_page_key').val());
  const oldSuccessHandler = feedbackAjax.success;
  feedbackAjax.success = (actionKey, action, isDeferred) => {
    if (thumbs === 'up') {
      setTimeout(() => {
        oldSuccessHandler.apply(feedbackAjax, [actionKey, action, isDeferred]);
        showStats();
      }, 200);
    } else {
      oldSuccessHandler.apply(feedbackAjax, [actionKey, action, isDeferred]);
      showStats();
    }
  };
  // wait to render new html until animation is done
  jqOffOn($container, 'submit', e => {
    e.preventDefault();
    feedbackAjax.form($container, {
      version: $container.find('#id_version').val(),
      component_key: $container.data()['cmpKey'],
      thumbs,
    });
  });

  // force submit of feedback form if user clicks next quiz
  if ($nextQuiz.length > 0) {
    jqOffOn($nextQuiz, 'click', e => {
      if ($container.find('textarea').length > 0) {
        e.preventDefault();
        if (!$textarea.val()) {
          $textarea.val(' ');
        }
        feedbackAjax.form($container, {
          version: $container.find('#id_version').val(),
          component_key: $container.data()['cmpKey'],
        });
      }
    });
  }
};

/**
 * Default vkey handler. This will replace the current default solvable vkey handler
 * @returns {JQuery}
 */
solvable.solvableVkeyHandler = function (action, action_key) {
  var $root = vkey.handle(action, action_key),
    $solvable = $root.parents('.solv-main'),
    ctx = findSolvableContext($solvable),
    $endstate = $solvable.find('.endstate'),
    isAttempted = $endstate.length > 0;

  $body.toggleClass('endstate', isAttempted);

  ctx.$solvable = $root.parents('.solv-main');
  ctx.solvableDidOnload = false; // reset onload status
  solvableContext[$solvable.data('solvable')] = ctx;

  // re-initialize the solvable controller since it's in the page instead of the component
  solvable.solvable($solvable);
  solvable.solvableRedesign($solvable);

  // reset the solution button if necessary
  resetSolutionButton(
    $('.btn-solution.dim,.btn-solution.disabled'),
    $('.solv-simple-solution,.solv-solutions').length > 0,
  );

  // update the recently solved by component (should already be loaded at this point)
  loadSolversComponent(ctx);

  latex.updateMathjax($root);

  if (isMobile && !$body.hasClass('prac-solvable')) {
    // if answered correct, make sure solution button knows that solutions
    // are already loaded
    const $solutionButton = $solvable.find('.btn-solution');
    if ($('.solv-details.endstate.correct').length) {
      $solutionButton.data('solutions-loaded', true);
    }

    // undo disabling solution button if it was dynamically disabled
    if ($solutionButton.data('originally-disabled') === false) {
      $solutionButton.removeClass('disabled');
    }
  } else if (isMobile) {
    solvable.solutionButton($solvable.find('.btn-solution').not('.btn-b2-flag'));
  }

  if (instantTryItYourself && isAttempted) {
    const $wikiCmp = jqGetElemById('cmp_wiki_canonical_page_id');
    if ($wikiCmp.length) {
      // update the wiki content since this solvable has been likely opened from
      // "Try it yourself" section
      const data = $wikiCmp.find('.summary').data();
      ajax(data['pageKey']).post(data['cmpUrl'], {
        page_key: data['pageKey'],
        component_key: data['cmpKey'],
      });
    }
  }

  // ----- Alex (11/6/2017): trying to get some more info on BP-14763 -----
  var $mcq = $root.find('.mcq.endstate .mcq');
  if ($mcq.length && clickedChoice && ctx.data && !ctx.data.canStillAnswer) {
    var registeredChoice = $mcq.data('registered-choice').toString();
    if (registeredChoice && clickedChoice !== registeredChoice) {
      Sentry.captureMessage('Registered choice does not match the corresponding clicked one', {
        tags: {
          '____Clicked.Choice': clickedChoice,
          '___Registered.Choice': registeredChoice,
          '__Answer.Is.Correct': ctx.data.answerIsCorrect,
          '_Problem.Url': window.location.href,
        },
      });
    }
  }
  clickedChoice = null;
  // ----------------------------------------------------------------------

  return $root;
};
vkey.assessment_public_solvable_logged_in = vkey.hundred_day_challenge_solvable_logged_in =
  solvable.solvableVkeyHandler;

/**
 * handles removing panes and prolems from frontend admin edit all problems page
 * this prevents all of the remove forms getting updated, and only updates the relevant one
 * @param {object} action
 * @param {string} actionKey
 */

vkey.quizzes_remove_solvable_from_quiz = vkey.quizzes_remove_pane_from_quiz = function (
  action,
  actionKey,
) {
  const content_id = $(action.new_html).find('.target_content_id').val();
  const $updateElement = $(`.target_content_id.${content_id}`).closest(`.cmp_${actionKey}_id`);
  if ($updateElement.length === 0) {
    window.vkey.handle(action, actionKey);
  } else {
    const newActionKey = `${actionKey}_${content_id}`;
    $updateElement.addClass(`cmp_${newActionKey}_id`);
    window.vkey.handle(action, newActionKey);
  }
};

vkey.quizzes_problemset = function () {
  var $root = solvable.solvableVkeyHandler.apply(vkey, arguments);
  solvable.cropActionsMenu($root);

  const $preloader = $root.parents('.prac-preload-wrapper');
  if ($preloader.length) {
    preloaders.practice($preloader, {
      reloaded: true,
    });
  }

  // scroll to end state if no solutions
  if (isMobile) {
    const $target = $root.find('.b2-solution');
    if (
      $target.length &&
      $target.is(':visible') &&
      jqGetElemById('solv-simple-solution').length === 0
    ) {
      Anchors.scrollToTarget($target);
    }
  } else {
    const $b2Solution = jqGetElemById('b2-solution');
    const isEndstate = $root.find('.solv-details').hasClass('endstate');
    if ($b2Solution.length && isEndstate) {
      const scrollKey = 'auto_scrolled_to_free_solution';
      if (wafer.get(scrollKey) !== true) {
        Anchors.scrollToTarget($b2Solution);
        wafer.set(scrollKey, true);
      }
    }
  }
  insterstitialPaneReloaded = false;
};
vkey.potw_problemset = vkey.potw_problemset_default = vkey.quizzes_problemset;

/** Common handler for solution and dispute discussions */
var solvable_discussions_vkey_handler = function (action, actionKey, state) {
  const $root = window.vkey.handle(action, actionKey);
  latex.updateMathjax($root.find('.latex'));

  comments.votes();
  comments.add_form();
  comments.subscribe();

  $root.find('.vote-trigger.with-tooltip').tooltip();

  // sometimes comments aren't deferred
  comments.events();

  // shrink large svgs
  latex.scaleSVG($root.find('.latexprocessor-block, .latexprocessor-inline'));

  const ctx = findSolvableContext($root.parents('.solv-main'));
  handleSolvDiscState(ctx, state);
};

/**
 * Generic solution vkey handler
 * @param {object} action
 * @param {object} action_key
 */
vkey.quizzes_challengesolvablesolutionlist =
  vkey.potw_solvablesolutionlist =
  vkey.solutiondiscussions_publicsolvablesolutionlist =
    function (action, action_key) {
      solvable_discussions_vkey_handler(action, action_key, 'solutions');
    };

vkey.potw_solvabledisputelist =
  vkey.quizzes_solvabledisputelist =
  vkey.dailyproblems_disputelist =
  vkey.disputediscussions_publicsolvabledisputelist =
    function (action, action_key) {
      solvable_discussions_vkey_handler(action, action_key, 'disputes');
    };

/**
 * Solution-adding handler
 * @param {object} action
 * @param {object} actionKey
 */
var add_solution_key_handler = function (action, actionKey) {
  const $root = window.vkey.handle(action, actionKey);
  latex.updateMathjax($root.find('.latex'));
  comments.add_form();
  return $root;
};

vkey.quizzes_challengesolvableaddsolutionpost =
  vkey.potw_addsolvabledisputepost =
  vkey.potw_solvableaddsolutionpost =
  vkey.quizzes_addsolvabledisputepost =
  vkey.groups_groupsolvableaddsolutionpost =
  vkey.solutiondiscussions_publicsolvableaddsolutionpost =
  vkey.disputediscussions_publicsolvableadddisputepost =
    add_solution_key_handler;

vkey.disputediscussions_publicsolvableadddisputepostmodal =
  vkey.potw_addsolvabledisputepostmodal =
  vkey.quizzes_addsolvabledisputepostmodal =
    function () {
      const $root = add_solution_key_handler.apply(vkey, arguments);
      initGenericControllers($root);
    };

vkey.quizzes_solvabledisputesubscribe =
  vkey.quizzes_challengesolvablesolutionsubscribe =
  vkey.disputediscussions_publicsolvabledisputesubscribe =
  vkey.solutiondiscussions_publicsolvablesolutionsubscribe =
    function (action, action_key) {
      vkey.handle(action, action_key);
      comments.subscribe();
    };

vkey.quizzes_interstitial_pane = vkey.admin_edit_interstitial_pane_component = function (
  action,
  action_key,
) {
  const chapterIntroState = $body.data(QUIZ_CHAPTER_INTRO_STATE);
  if (chapterIntroState && chapterIntroState.active === true) {
    // Don't do anything for the chapter intro, just mark that one less pane is continuing
    $body.data(QUIZ_CHAPTER_INTRO_STATE, {
      active: true,
      numContinuingPanes: chapterIntroState.numContinuingPanes - 1,
    });
    return;
  }

  const $root = vkey.handle(action, action_key);
  const $preloader = $root.parents('.prac-preload-wrapper');
  if ($preloader.length) {
    let useTargetPosition;

    Object.values(CMP_RELOADING_FLAGS).forEach(flag => {
      if ($body.data(flag) === true) {
        useTargetPosition = false;
        $body.data(flag, false);
      }
    });

    if (useTargetPosition === undefined) {
      useTargetPosition =
        !$root.find('.solv-pane.is-editing-pane').length && !jqGetElemById('sales-card').length;
    }

    preloaders.practice($preloader, {
      reloaded: true,
      useTargetPosition,
    });
  }
  insterstitialPaneReloaded = true;
};

module.exports = solvable;
